/**
 * DashboardPriorityRow — Bloomberg-style "what needs your attention NOW" band
 * shown at the very top of the dashboard.
 *
 * Renders 4 priority cards tailored to the viewer:
 *
 * Property Manager:
 *   • Applications Requiring Review
 *   • Open Risk Indicators
 *   • Decision-Ready Applications
 *   • Additional Information Needed
 *
 * NTN Staff:
 *   • Verification Queue
 *   • Validation Queue
 *   • Open Risk Indicators
 *   • Decision-Ready Applications
 *   • Bottlenecks
 *
 * Cards are clickable — they wire to the dashboard's existing
 * stageFilter / riskFilter setters so users can drill down instantly.
 */
import {
  AlertOctagon, ShieldAlert, Gavel, Hourglass, ListChecks, FileSearch,
  Clock4, Sparkles, ArrowRight,
} from "lucide-react";

const TONE_STYLE = {
  rose:    { bg: "bg-rose-50",    text: "text-rose-700",    ring: "ring-rose-200",    dot: "bg-rose-500",    accent: "border-l-rose-500"    },
  amber:   { bg: "bg-amber-50",   text: "text-amber-700",   ring: "ring-amber-200",   dot: "bg-amber-500",   accent: "border-l-amber-500"   },
  violet:  { bg: "bg-violet-50",  text: "text-violet-700",  ring: "ring-violet-200",  dot: "bg-violet-500",  accent: "border-l-violet-500"  },
  blue:    { bg: "bg-blue-50",    text: "text-blue-700",    ring: "ring-blue-200",    dot: "bg-blue-500",    accent: "border-l-blue-500"    },
  emerald: { bg: "bg-emerald-50", text: "text-emerald-700", ring: "ring-emerald-200", dot: "bg-emerald-500", accent: "border-l-emerald-500" },
  slate:   { bg: "bg-slate-50",   text: "text-slate-700",   ring: "ring-slate-200",   dot: "bg-slate-500",   accent: "border-l-slate-500"   },
};

function PriorityCard({ label, value, hint, tone, Icon, onClick, testid, urgent }) {
  const t = TONE_STYLE[tone] || TONE_STYLE.slate;
  const clickable = !!onClick;
  const Tag = clickable ? "button" : "div";
  return (
    <Tag
      type={clickable ? "button" : undefined}
      onClick={onClick}
      className={`group relative w-full text-left bg-white rounded-lg shadow-sm ring-1 ring-slate-200 transition-all border-l-4 ${t.accent} p-5
        ${clickable ? "hover:shadow-md hover:-translate-y-0.5 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" : ""}
        ${urgent && value > 0 ? "ring-2 ring-amber-300/60" : ""}`}
      data-testid={testid}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500 flex items-center gap-1.5">
            <span className={`h-1.5 w-1.5 rounded-full ${t.dot}`} />
            {label}
          </div>
          <div className="font-serif text-4xl tracking-tight mt-2 leading-none text-slate-900 tabular-nums" data-testid={`${testid}-value`}>
            {value}
          </div>
          {hint && <div className="text-[11px] text-slate-500 mt-2 leading-snug">{hint}</div>}
        </div>
        {Icon && (
          <div className={`h-10 w-10 rounded-lg ${t.bg} ring-1 ${t.ring} flex items-center justify-center shrink-0`}>
            <Icon className={`h-4.5 w-4.5 ${t.text}`} strokeWidth={1.8} />
          </div>
        )}
      </div>
      {clickable && value > 0 && (
        <div className="absolute right-3 bottom-3 text-slate-300 group-hover:text-slate-700 transition-colors">
          <ArrowRight className="h-3.5 w-3.5" />
        </div>
      )}
    </Tag>
  );
}

export default function DashboardPriorityRow({
  isStaff, stats, onStageFilter, onRiskFilter, testid = "dashboard-priority-row",
}) {
  // Header copy adapts to role for the "command center" feel.
  const eyebrow = isStaff ? "NTN Staff · Command Center" : "Property Manager · Priorities";
  const headline = isStaff
    ? "What needs the NTN team's attention"
    : "What needs your attention";

  const cards = isStaff ? [
    {
      label: "Verification Queue",
      value: stats.verification_queue ?? 0,
      hint: "Screening & Reports in progress",
      tone: "blue",
      Icon: FileSearch,
      onClick: () => onStageFilter("verify"),
      testid: "priority-verification-queue",
    },
    {
      label: "Validation Queue",
      value: stats.validation_queue ?? 0,
      hint: "Verification & Validation in progress",
      tone: "violet",
      Icon: ListChecks,
      onClick: () => onStageFilter("validation"),
      testid: "priority-validation-queue",
    },
    {
      label: "Open Risk Indicators",
      value: stats.risk_indicators_requiring_review ?? 0,
      hint: "Indicators awaiting NTN review",
      tone: "amber",
      Icon: ShieldAlert,
      onClick: () => onRiskFilter("requiring_review"),
      testid: "priority-open-risk-indicators",
      urgent: true,
    },
    {
      label: "Decision-Ready Applications",
      value: stats.decision_ready_reviews ?? 0,
      hint: "Awaiting an NTN recommendation",
      tone: "emerald",
      Icon: Gavel,
      onClick: () => onStageFilter("ready"),
      testid: "priority-decision-ready",
      urgent: true,
    },
    {
      label: "Bottlenecks",
      value: stats.bottlenecks ?? 0,
      hint: "Untouched > 7 days · not complete",
      tone: "rose",
      Icon: Clock4,
      onClick: undefined,  // No single-filter mapping; informational only.
      testid: "priority-bottlenecks",
      urgent: true,
    },
  ] : [
    {
      label: "Applications Requiring Review",
      value: stats.applications_requiring_attention ?? 0,
      hint: "Decision-Ready · Waiting on you",
      tone: "rose",
      Icon: AlertOctagon,
      onClick: () => onStageFilter("ready"),
      testid: "priority-requiring-attention",
      urgent: true,
    },
    {
      label: "Open Risk Indicators",
      value: stats.risk_indicators_requiring_review ?? 0,
      hint: "Indicators flagged on your applicants",
      tone: "amber",
      Icon: ShieldAlert,
      onClick: () => onRiskFilter("requiring_review"),
      testid: "priority-open-risk-indicators",
      urgent: true,
    },
    {
      label: "Decision-Ready Applications",
      value: stats.decision_ready_reviews ?? 0,
      hint: "NTN has finished verifying — your move",
      tone: "emerald",
      Icon: Gavel,
      onClick: () => onStageFilter("ready"),
      testid: "priority-decision-ready",
    },
    {
      label: "Additional Information Needed",
      value: stats.applications_waiting_on_info ?? 0,
      hint: "Outstanding info request",
      tone: "violet",
      Icon: Hourglass,
      onClick: () => onStageFilter("additional"),
      testid: "priority-waiting-on-info",
    },
  ];

  return (
    <section className="mb-6" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="h-3.5 w-3.5 text-amber-500" />
        <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500">{eyebrow}</span>
        <span className="text-slate-300">·</span>
        <span className="text-[11px] text-slate-600 font-medium" data-testid={`${testid}-headline`}>{headline}</span>
      </div>
      <div className={`grid grid-cols-2 ${cards.length === 5 ? "md:grid-cols-5" : "md:grid-cols-4"} gap-3`}>
        {cards.map((c) => <PriorityCard key={c.testid} {...c} />)}
      </div>
    </section>
  );
}
